import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import Footer from '../components/shared/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Search, ShoppingCart, Calculator, FileText, Truck, Landmark,
  Globe, ArrowRight, Check, Star, MapPin, TrendingUp, ChevronRight,
  Wheat, Factory, Shirt, Pill, Building2, Laptop, Palette, Cog,
  Users, Shield, Zap, ChevronLeft
} from 'lucide-react';

// Banner images
const HERO_BANNER_1 = "https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/kv0xkcdg_Untitled%20design%20%284%29.png";
const HERO_BANNER_2 = "https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/489wftd0_ATFF%20USER%20WEBSITE%20BANNER%20PICTURE.png";

const categories = [
  { id: 'agriculture', key: 'agriculture', icon: Wheat, color: 'bg-green-500' },
  { id: 'agro-processing', key: 'agroProcessing', icon: Factory, color: 'bg-amber-500' },
  { id: 'manufacturing', key: 'manufacturing', icon: Cog, color: 'bg-blue-500' },
  { id: 'textiles', key: 'textiles', icon: Shirt, color: 'bg-purple-500' },
  { id: 'pharmaceuticals', key: 'pharmaceuticals', icon: Pill, color: 'bg-red-500' },
  { id: 'construction', key: 'construction', icon: Building2, color: 'bg-orange-500' },
  { id: 'digital-services', key: 'digitalServices', icon: Laptop, color: 'bg-cyan-500' },
  { id: 'creative-industries', key: 'creativeIndustries', icon: Palette, color: 'bg-pink-500' },
];

const platformFeatures = [
  { icon: ShoppingCart, title: 'Buy & Sell', key: 'buyAndSell', href: '/marketplace' },
  { icon: Calculator, title: 'AfCFTA Tariffs', key: 'afcftaTariffs', href: '/tariff-calculator' },
  { icon: FileText, title: 'Customs Docs', key: 'customsDocs', href: '/documents' },
  { icon: Truck, title: 'Logistics', key: 'logisticsFeature', href: '/logistics' },
  { icon: Landmark, title: 'Trade Finance', key: 'tradeFinanceFeature', href: '/trade-finance' },
  { icon: Globe, title: 'Trade Nodes', key: 'tradeNodes', href: '#' },
];

const opportunities = [
  { market: 'Kenya', demand: 'Processed Foods', trend: '+23%', flag: '🇰🇪' },
  { market: 'Morocco', demand: 'Construction Materials', trend: '+18%', flag: '🇲🇦' },
  { market: 'South Africa', demand: 'Textiles & Garments', trend: '+31%', flag: '🇿🇦' },
  { market: 'Egypt', demand: 'Agricultural Products', trend: '+15%', flag: '🇪🇬' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [stats, setStats] = useState({
    businesses: '12,500+',
    countries: '54',
    tradeVolume: '$2.5B+',
    savings: '90%'
  });

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 2);
    }, 8000); // Change slide every 8 seconds
    return () => clearInterval(interval);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % 2);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + 2) % 2);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/marketplace?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Slider Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="relative h-[600px] md:h-[700px] lg:h-[750px]">
          {/* Slide 1 - ATFF User Website Banner (Clean - No Text Overlay) */}
          <div 
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              currentSlide === 0 ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <div className="absolute inset-0">
              <img 
                src={HERO_BANNER_2} 
                alt="Africa Trade Connect - Continental Trade Platform" 
                className="w-full h-full object-cover object-center"
              />
              {/* Subtle gradient at bottom for buttons visibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            </div>
            
            {/* CTA Buttons for Slide 1 - positioned at bottom */}
            <div className="absolute bottom-20 md:bottom-28 left-0 right-0 z-20">
              <div className="container-main">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button 
                    onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
                    className="min-w-[16rem] h-14 text-lg font-semibold bg-continental hover:bg-continental/90 text-white rounded-md shadow-xl px-6"
                    data-testid="slide1-start-trading-btn"
                  >
                    {t('home.startTrading')}
                  </Button>
                  <Button 
                    onClick={() => navigate('/marketplace')}
                    className="min-w-[16rem] h-14 text-lg font-semibold bg-tradeGold hover:bg-tradeGold/90 text-black rounded-md shadow-xl px-6"
                    data-testid="slide1-explore-markets-btn"
                  >
                    {t('home.exploreAfricanMarkets')}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Slide 2 - Original Green Gradient Hero with Text */}
          <div 
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              currentSlide === 1 ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <div className="gradient-hero h-full">
              {/* Faded background image */}
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1759272548470-d0686d071036?w=1920')] bg-cover bg-center opacity-10"></div>
              <div className="container-main relative h-full flex items-center">
                <div className="max-w-3xl py-16">
                  <Badge className="bg-tradeGold text-black mb-6" data-testid="hero-badge">
                    {t('home.heroBadge')}
                  </Badge>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 font-poppins">
                    {t('home.heroTitle')}{' '}
                    <span className="text-tradeGold">{t('home.heroTitleHighlight')}</span>
                  </h1>
                  <p className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed max-w-2xl">
                    {t('home.heroDescription')}
                  </p>

                  {/* Search Bar */}
                  <form onSubmit={handleSearch} className="mb-8">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative flex-1">
                        <Search className="absolute start-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          type="text"
                          placeholder={t('home.searchPlaceholder')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="ps-12 h-14 text-lg bg-white border-0 shadow-lg"
                          data-testid="hero-search-input"
                        />
                      </div>
                      <Button type="submit" className="h-14 px-8 btn-secondary" data-testid="hero-search-btn">
                        <Search className="w-5 h-5 me-2" />
                        {t('common.search')}
                      </Button>
                    </div>
                  </form>

                  {/* CTA Buttons */}
                  <div className="flex flex-wrap gap-4">
                    <Button 
                      onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')} 
                      className="btn-secondary"
                      data-testid="hero-register-btn"
                    >
                      {isAuthenticated ? t('home.goToDashboard') : t('home.registerBusiness')}
                      <ArrowRight className="w-4 h-4 ms-2 rtl-flip" />
                    </Button>
                    <Button 
                      onClick={() => navigate('/marketplace')} 
                      variant="outline" 
                      className="bg-white/10 border-white text-white hover:bg-white hover:text-charcoal"
                      data-testid="hero-marketplace-btn"
                    >
                      {t('home.exploreMarketplace')}
                    </Button>
                    <Button 
                      onClick={() => navigate('/tariff-calculator')} 
                      variant="outline" 
                      className="bg-white/10 border-white text-white hover:bg-white hover:text-charcoal"
                      data-testid="hero-tariff-btn"
                    >
                      {t('home.calculateTariffs')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Slider Navigation Arrows */}
          <button 
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center transition-all"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button 
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center transition-all"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          {/* Slider Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-3">
            {[0, 1].map((index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  currentSlide === index 
                    ? 'bg-tradeGold w-8' 
                    : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-white shadow-lg">
          <div className="container-main py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center" data-testid="stat-businesses">
                <p className="text-3xl font-bold text-continental font-poppins" dir="ltr">{stats.businesses}</p>
                <p className="text-sm text-gray-500">{t('home.stats.businesses')}</p>
              </div>
              <div className="text-center" data-testid="stat-countries">
                <p className="text-3xl font-bold text-continental font-poppins" dir="ltr">{stats.countries}</p>
                <p className="text-sm text-gray-500">{t('home.stats.countries')}</p>
              </div>
              <div className="text-center" data-testid="stat-volume">
                <p className="text-3xl font-bold text-continental font-poppins" dir="ltr">{stats.tradeVolume}</p>
                <p className="text-sm text-gray-500">{t('home.stats.tradeVolume')}</p>
              </div>
              <div className="text-center" data-testid="stat-savings">
                <p className="text-3xl font-bold text-tradeGold font-poppins" dir="ltr">{stats.savings}</p>
                <p className="text-sm text-gray-500">{t('home.stats.tariffReduction')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="section-padding bg-white" data-testid="features-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-continental/10 text-continental">{t('home.features.badge')}</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4 font-poppins">
              {t('home.features.title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('home.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {platformFeatures.map((feature) => (
              <Link to={feature.href} key={feature.title}>
                <Card className="card-interactive h-full text-center" data-testid={`feature-${feature.title.toLowerCase().replace(/\s/g, '-')}`}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 mx-auto mb-4 bg-continental/10 rounded-xl flex items-center justify-center">
                      <feature.icon className="w-7 h-7 text-continental" />
                    </div>
                    <h3 className="font-semibold text-charcoal mb-1">{t(`home.features.${feature.key}`)}</h3>
                    <p className="text-sm text-gray-500">{t(`home.features.${feature.key}Desc`)}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="section-padding bg-gray-50" data-testid="categories-section">
        <div className="container-main">
          <div className="flex items-end justify-between mb-10">
            <div>
              <Badge className="mb-4 bg-tradeGold/10 text-tradeGold-foreground">{t('home.categories.badge')}</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal font-poppins">
                {t('home.categories.title')}
              </h2>
            </div>
            <Link to="/marketplace" className="hidden md:flex items-center gap-2 text-continental font-medium hover:gap-3 transition-all">
              {t('home.categories.viewAll')} <ChevronRight className="w-4 h-4 rtl-flip" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category) => (
              <Link to={`/marketplace?category=${category.id}`} key={category.id}>
                <Card className="card-interactive" data-testid={`category-${category.id}`}>
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${category.color}`}>
                      <category.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-charcoal">{t(`home.categories.${category.key}`)}</h3>
                      <p className="text-sm text-gray-500">{t('home.categories.browseProducts')}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trade Opportunities */}
      <section className="section-padding bg-charcoal text-white" data-testid="opportunities-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-tradeGold text-black">{t('home.opportunities.badge')}</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-poppins">
              {t('home.opportunities.title')}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t('home.opportunities.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {opportunities.map((opp) => (
              <Card key={opp.market} className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors" data-testid={`opportunity-${opp.market}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">{opp.flag}</span>
                    <div>
                      <h3 className="font-semibold text-white">{opp.market}</h3>
                      <p className="text-sm text-gray-400">{t('home.opportunities.importing')}</p>
                    </div>
                  </div>
                  <p className="text-lg font-medium text-tradeGold mb-2">{opp.demand}</p>
                  <div className="flex items-center gap-2 text-green-400">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-sm font-medium">{opp.trend} {t('home.opportunities.demandGrowth')}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button onClick={() => navigate('/marketplace')} className="btn-secondary">
              {t('home.opportunities.exploreAll')}
              <ArrowRight className="w-4 h-4 ms-2 rtl-flip" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why ATFF */}
      <section className="section-padding bg-white" data-testid="why-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-continental/10 text-continental">{t('home.whyAtff.badge')}</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-6 font-poppins">
                {t('home.whyAtff.title')}
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {t('home.whyAtff.description')}
              </p>

              <div className="space-y-4">
                {[
                  { icon: Users, titleKey: 'verifiedTraders', descKey: 'verifiedTradersDesc' },
                  { icon: Shield, titleKey: 'afcftaCompliance', descKey: 'afcftaComplianceDesc' },
                  { icon: Zap, titleKey: 'fastSettlements', descKey: 'fastSettlementsDesc' },
                ].map((item) => (
                  <div key={item.titleKey} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-continental/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-continental" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-charcoal">{t(`home.whyAtff.${item.titleKey}`)}</h4>
                      <p className="text-sm text-gray-500">{t(`home.whyAtff.${item.descKey}`)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button onClick={() => navigate('/register')} className="btn-primary mt-8">
                {t('home.whyAtff.getStarted')}
                <ArrowRight className="w-4 h-4 ms-2 rtl-flip" />
              </Button>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1686146711957-04aec031396b?w=600&h=400&fit=crop"
                alt="African business team"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-tradeGold rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-charcoal font-poppins">4.9/5</p>
                    <p className="text-sm text-gray-500">{t('home.whyAtff.traderRating')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-continental" data-testid="cta-section">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-poppins">
            {t('home.cta.title')}
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/register')} className="btn-secondary">
              {t('home.cta.registerBusiness')}
              <ArrowRight className="w-4 h-4 ms-2 rtl-flip" />
            </Button>
            <Button 
              onClick={() => navigate('/marketplace')} 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-continental"
            >
              {t('home.cta.browseMarketplace')}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
